import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '../api';
import AddTaskIcon from '@mui/icons-material/AddTask';

interface AIInsight {
  id: string;
  generated_by_agent: string;
  insight_text: string;
  created_at: string;
}

interface ActionableInsightsFeedProps {
  insights: AIInsight[];
  dealId: string;
}

interface CreateTaskData {
  description: string;
  deal_id: string;
}

const createTaskFromInsight = (data: CreateTaskData) => {
  return tasksApi.createTask(data);
};

export default function ActionableInsightsFeed({ insights, dealId }: ActionableInsightsFeedProps) {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  
  const mutation = useMutation({
    mutationFn: createTaskFromInsight,
    onSuccess: () => {
      setSuccessMessage("Task created successfully!");
      // Invalidate tasks query to refresh the task list
      queryClient.invalidateQueries({ queryKey: ['tasks', dealId] });
    }
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const getAgentColor = (agent: string) => {
    switch (agent) {
      case 'CEO Agent':
        return 'primary';
      case 'CRO Agent':
        return 'secondary';
      case 'CSO Agent':
        return 'success';
      default:
        return 'default';
    }
  };

  if (!insights || insights.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>C-Suite AI Advisory</Typography>
        <Card>
          <CardContent>
            <Typography color="textSecondary">No insights available yet.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>C-Suite AI Advisory</Typography>
      {insights.map(insight => (
        <Card key={insight.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Chip 
                label={insight.generated_by_agent} 
                color={getAgentColor(insight.generated_by_agent) as any}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(insight.created_at).toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ my: 1 }}>
              {insight.insight_text}
            </Typography>
            <Button 
              variant="outlined" 
              size="small"
              sx={{ mt: 2 }}
              startIcon={<AddTaskIcon />}
              onClick={() => mutation.mutate({ 
                description: insight.insight_text, 
                deal_id: dealId 
              })}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creating...' : 'Create Task from this Insight'}
            </Button>
          </CardContent>
        </Card>
      ))}
      
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
} 